import { ElementRef, Injectable } from '@angular/core';
import { DropdownNavigationConfig } from '../models/DropdownNavigationConfig';
import { HotkeyConfig } from '../models/HotkeyConfig';
import { KEY_ARROW_DOWN, KEY_ARROW_UP, KEY_ENTER, KEY_ESCAPE } from '../models/HotkeyDefinitions';

@Injectable({
    providedIn: 'root'
})
export class HotkeyService {

    isHotkeyMatch(event: KeyboardEvent, config: HotkeyConfig): boolean {
        if (!event.key || !config.key) {
            return false;
        }
        const keyMatch = event.key.toLowerCase() === config.key.toLowerCase();
        const ctrlMatch = (config.ctrl ?? false) === (event.ctrlKey || event.metaKey);
        const shiftMatch = (config.shift ?? false) === event.shiftKey;
        const altMatch = (config.alt ?? false) === event.altKey;

        return keyMatch && ctrlMatch && shiftMatch && altMatch;
    }

    handleHotkeys(event: KeyboardEvent, hotkeys: HotkeyConfig[]): boolean {
        for (const hotkey of hotkeys) {
            if (this.isHotkeyMatch(event, hotkey)) {
                if (hotkey.preventDefault ?? true) {
                    event.preventDefault();
                    event.stopPropagation();
                }
                hotkey.action();
                return true;
            }
        }
        return false;
    }

    handleCheckboxEnter(event: KeyboardEvent): boolean {
        const target = event.target as HTMLElement;
        if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox') {
            event.preventDefault();
            event.stopPropagation();
            (target as HTMLInputElement).click();
            return true;
        }
        return false;
    }

    handleDropdownKeyboard<T>(event: KeyboardEvent, config: DropdownNavigationConfig<T>): boolean {
        const key = event.key;
        const itemCount = config.items.length;

        if (key === KEY_ARROW_DOWN) {
            event.preventDefault();
            event.stopPropagation();
            const newIndex = Math.min(config.highlightIndex + 1, itemCount - 1);
            config.onHighlightChange(newIndex);
            this.scrollHighlightedIntoView(config.containerRef, newIndex);
            return true;
        }

        if (key === KEY_ARROW_UP) {
            event.preventDefault();
            event.stopPropagation();
            const newIndex = Math.max(config.highlightIndex - 1, 0);
            config.onHighlightChange(newIndex);
            this.scrollHighlightedIntoView(config.containerRef, newIndex);
            return true;
        }

        if (key === KEY_ENTER && config.highlightIndex >= 0 && config.highlightIndex < itemCount) {
            event.preventDefault();
            event.stopPropagation();
            config.onSelect(config.items[config.highlightIndex]);
            config.onHighlightChange(-1);
            return true;
        }

        if (key === KEY_ESCAPE) {
            event.preventDefault();
            event.stopPropagation();
            config.onClose();
            this.focusDropdownButton(config.containerRef);
            return true;
        }

        return false;
    }

    scrollHighlightedIntoView(containerRef: ElementRef, index: number): void {
        setTimeout(() => {
            const container = containerRef?.nativeElement;
            if (!container) return;
            const scrollableDiv = container.querySelector('.table-responsive') as HTMLElement;
            const thead = container.querySelector('thead') as HTMLElement;
            const rows = container.querySelectorAll('tbody tr');
            if (rows[index] && scrollableDiv) {
                const row = rows[index] as HTMLElement;
                const headerHeight = thead ? thead.offsetHeight : 0;
                const rowOffsetTop = row.offsetTop - headerHeight;
                const rowOffsetBottom = rowOffsetTop + row.offsetHeight;
                const visibleTop = scrollableDiv.scrollTop;
                const visibleBottom = visibleTop + scrollableDiv.clientHeight - headerHeight;

                if (rowOffsetTop < visibleTop) {
                    scrollableDiv.scrollTop = rowOffsetTop;
                } else if (rowOffsetBottom > visibleBottom) {
                    scrollableDiv.scrollTop = rowOffsetBottom - scrollableDiv.clientHeight + headerHeight;
                }
            }
        });
    }

    focusDropdownButton(containerRef: ElementRef): void {
        const button = containerRef?.nativeElement?.querySelector('button');
        if (button) {
            button.focus();
        }
    }
}
